import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Import translation hook
import { API_BASE } from '../config';
import ExecutionModal from './ExecutionModal';
import EditWorkoutModal from './EditWorkoutModal';
import CompactTrainingCalendar from './Plans/CompactTrainingCalendar';
import WorkoutDetailModal from './Plans/WorkoutDetailModal';
import MissedWorkoutModal from './Plans/MissedWorkoutModal';
import HrZonesModal from './Plans/HrZonesModal';
import PowerZonesModal from './Plans/PowerZonesModal';
import SelectedWorkoutPanel from './Plans/SelectedWorkoutPanel';
import MoveWorkoutModal from './Plans/MoveWorkoutModal';
import DayWorkoutsChooserModal from './Plans/DayWorkoutsChooserModal';
import { HiPencilSquare, HiClock, HiCog6Tooth, HiChatBubbleLeftRight, HiChevronDown, HiChevronUp, HiFlag } from 'react-icons/hi2';
import PlanVisibilityModal from './Plans/PlanVisibilityModal';
import DeletePlanModal from './Plans/DeletePlanModal';
import MobilePlanTopCarousel from './Plans/MobilePlanTopCarousel';
import FeaturedWorkoutsBlock from './Plans/FeaturedWorkoutsBlock';
import Markdown from './common/Markdown';
import CoachChatDrawer from './CoachChat/CoachChatDrawer';
import MobileBottomTabs from './Plans/MobileBottomTabs';
import CoachChatPanel from './CoachChat/CoachChatPanel';
import PlanHistoryModal from './Plans/PlanHistoryModal';
import PlanGenerationScreen from './Plans/PlanGeneration/PlanGenerationScreen';
import PlanEndModal from './Plans/PlanEndModal';
import CoachAnalysisRenderer from './Plans/CoachAnalysisRenderer';
import AddEventModal from './Plans/AddEventModal';
import EventsPanel from './Plans/EventsPanel';
import CoachOptionsModal from './Plans/CoachOptionsModal';
import { formatShortDate } from '../utils/plan_changes';
import { getLastPlannedWorkoutDateISO, isPlanClosedStatus } from '../utils/plan_progress';

const WORKOUT_COLORS = {
    run: 'bg-green-500',
    long_run: 'bg-purple-600',
    workout: 'bg-orange-500',
    race: 'bg-red-600',
    recovery: 'bg-green-300',
    rest: 'bg-gray-200',
    cross_train: 'bg-blue-400',
    cross_train_bike: 'bg-sky-500',
    bike: 'bg-sky-600',

    // Triathlon
    swim: 'bg-cyan-500',
    brick: 'bg-pink-500',

    // Bike
    ride_easy: 'bg-green-500',
    long_ride: 'bg-purple-600',
    sweet_spot: 'bg-orange-500',
    threshold: 'bg-orange-600',
    intervals: 'bg-red-500',
    skills: 'bg-blue-400',
    strength: 'bg-amber-500'
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const COACH_STRATEGY_COLLAPSED_LS_KEY = 'runcoach:coachStrategyCollapsed';

const PlanView = () => {
  const { t, i18n } = useTranslation();
  const { planId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completedWorkouts, setCompletedWorkouts] = useState([]);
  const [skippedWorkouts, setSkippedWorkouts] = useState([]);
  // Only the owner can edit / access plan settings actions. Default to false until plan is loaded.
  const canEdit = planData?.can_edit === true;
  const planStatus = String(planData?.status || '');
  const planIsClosed = isPlanClosedStatus(planStatus);
  const canManagePlan = canEdit && !planIsClosed;

  // Calculate current date once
  const todayDate = new Date().toISOString().split('T')[0];

  // Coach Strategy collapse state (global across plans)
  const [isCoachStrategyCollapsed, setIsCoachStrategyCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COACH_STRATEGY_COLLAPSED_LS_KEY) === '1';
    } catch (e) {
      return false;
    }
  });

  // Mobile tabs (home/calendar/chat)
  // Sync with URL query parameter
  const getTabFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'calendar' || tab === 'chat') return tab;
    return 'home';
  };

  const [mobileTab, setMobileTab] = useState(getTabFromUrl());

  useEffect(() => {
    const tab = getTabFromUrl();
    if (tab !== mobileTab) {
      setMobileTab(tab);
    }
  }, [location.search]);

  const handleMobileTabChange = (newTab) => {
    setMobileTab(newTab);
    const params = new URLSearchParams(location.search);
    if (newTab === 'home') params.delete('tab');
    else params.set('tab', newTab);
    navigate({ search: params.toString() }, { replace: true });
  };


  // Modification Modal State
  const [showModModal, setShowModModal] = useState(false);
  const [modRequest, setModRequest] = useState('');
  const [lastModRequest, setLastModRequest] = useState(''); // To show during generation
  const [modAnalysis, setModAnalysis] = useState(null);
  const [isModAnalyzing, setIsModAnalyzing] = useState(false);
  const [isApplyingMod, setIsApplyingMod] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  // History & Versions
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [reverting, setReverting] = useState(false);
  // Single source of truth: we only display the current plan (plan_json).

  // Completion Modal
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedWorkoutForCompletion, setSelectedWorkoutForCompletion] = useState(null);

  // Workout details modal (mobile + desktop)
  const [showWorkoutDetailsModal, setShowWorkoutDetailsModal] = useState(false);
  const [selectedWorkoutForDetails, setSelectedWorkoutForDetails] = useState(null);
  const [executedPreview, setExecutedPreview] = useState({ workoutId: null, debrief: null, loading: false, error: null });
  const executedPreviewAbortRef = useRef(null);

  // Day chooser modal (when multiple workouts share the same day)
  const [showDayWorkoutsChooser, setShowDayWorkoutsChooser] = useState(false);
  const [dayWorkoutsContext, setDayWorkoutsContext] = useState(null); // { weekNumber, dayName, date, workouts }

  // Missed workout modal (collect reason)
  const [showMissedModal, setShowMissedModal] = useState(false);
  const [selectedWorkoutForMissed, setSelectedWorkoutForMissed] = useState(null);
  const [missedMode, setMissedMode] = useState('coach');

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedWorkoutForEdit, setSelectedWorkoutForEdit] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showHrZonesModal, setShowHrZonesModal] = useState(false);
  const [savingHrZones, setSavingHrZones] = useState(false);
  const [showPowerZonesModal, setShowPowerZonesModal] = useState(false);
  const [savingPowerZones, setSavingPowerZones] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [showDeletePlanModal, setShowDeletePlanModal] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(false);
  const [showPlanEndModal, setShowPlanEndModal] = useState(false);
  const [planEndMode, setPlanEndMode] = useState(null);
  const [endingPlan, setEndingPlan] = useState(false);

  // Coach chat (free-form, can propose plan changes)
  const [showCoachChatModal, setShowCoachChatModal] = useState(false);

  // Intermediate events + coach v2 options (missed session / event replan)
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [eventOptionsId, setEventOptionsId] = useState(null);
  const [missedCoach, setMissedCoach] = useState(null); // { analysis, sourceId, title, date }

  const isDesktop = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 768px)').matches;
  const isMobile = !isDesktop;
  const isChatTabMobile = Boolean(isMobile && mobileTab === 'chat');
  const isCalendarTabMobile = Boolean(isMobile && mobileTab === 'calendar');

  const pollIntervalRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const fetchPlan = useCallback(() => {
    return fetch(`${API_BASE}/plans/${planId}`, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error("Plan not found");
        return res.json();
      })
      .then(data => {
        setPlanData(data);
        setLoading(false);

        // Auto-expand current week
        if (data.plan && data.plan.weeks) {
          let targetWeek = null;

          // Strategy 1: Match date in workouts (if available)
          // We try to find a workout that has today's date
          const weekWithToday = data.plan.weeks.find(w =>
            w.workouts && w.workouts.some(day => day.date === todayDate)
          );

          if (weekWithToday) {
            targetWeek = weekWithToday.week_number;
          }

          // Strategy 2: Calculate from start_date if strategy 1 failed
          if (!targetWeek && data.start_date) {
            const start = new Date(data.start_date);
            const now = new Date();
            // Reset hours to compare dates only
            start.setHours(0, 0, 0, 0);
            now.setHours(0, 0, 0, 0);

            const diffTime = now - start;
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            // Week 1 is days 0-6, Week 2 is 7-13, etc.
            const calcWeek = Math.floor(diffDays / 7) + 1;

            if (calcWeek >= 1 && calcWeek <= data.plan.weeks.length) {
              targetWeek = calcWeek;
            }
          }
        }
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
        stopPolling();
      });
  }, [planId, stopPolling, todayDate]);

  const saveVisibility = async (visibility) => {
    if (!canManagePlan) return;
    setSavingVisibility(true);
    try {
      const res = await fetch(`${API_BASE}/plans/${planId}/visibility`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ visibility })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to update visibility');
      setPlanData((prev) => ({ ...prev, visibility: data.visibility }));
      setShowVisibilityModal(false);
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Failed to update visibility');
    } finally {
      setSavingVisibility(false);
    }
  };

  // Initial load (and when planId changes)
  useEffect(() => {
    fetchPlan();
    return () => stopPolling();
  }, [fetchPlan, stopPolling]);

  // Poll whenever the plan enters a generating state (including after applying a modification)
  useEffect(() => {
    const status = String(planData?.status || '');
    const isGenerating = status === 'generating' || status.startsWith('generating_');
    if (!isGenerating) {
      stopPolling();
      return;
    }

    // Ensure we refresh quickly when we just entered generating
    fetchPlan();

    if (!pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(fetchPlan, 3000);
    }

    return () => stopPolling();
  }, [fetchPlan, planData?.status, stopPolling]);

  useEffect(() => {
    let cancelled = false;
    const fetchCompleted = async () => {
      try {
        const res = await fetch(`${API_BASE}/plans/${planId}/workouts`, { credentials: 'include' });
        const data = await res.json();
        const rows = Array.isArray(data) ? data : [];
        if (!cancelled) {
          setCompletedWorkouts(rows.filter(w => w.status === 'completed'));
          setSkippedWorkouts(rows.filter(w => w.status === 'skipped'));
        }
      } catch (e) {
        // Non-blocking: plan view should still work even if this fails
        if (!cancelled) {
          setCompletedWorkouts([]);
          setSkippedWorkouts([]);
        }
      }
    };
    fetchCompleted();
    return () => { cancelled = true; };
  }, [planId]);

  // Desktop only: when a selected planned session is completed, load a lightweight preview
  // (executed session summary + user feedback + coach summary). "View details" opens the full page.
  useEffect(() => {
    const isDesktop =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(min-width: 768px)').matches;

    if (!isDesktop) {
      // Avoid background fetches on mobile (modal covers the details flow there).
      if (executedPreviewAbortRef.current) executedPreviewAbortRef.current.abort();
      executedPreviewAbortRef.current = null;
      setExecutedPreview({ workoutId: null, debrief: null, loading: false, error: null });
      return;
    }

    const planned = selectedWorkoutForDetails;
    if (!planned) {
      if (executedPreviewAbortRef.current) executedPreviewAbortRef.current.abort();
      executedPreviewAbortRef.current = null;
      setExecutedPreview({ workoutId: null, debrief: null, loading: false, error: null });
      return;
    }

    // Completed workouts are stored with id = planned workout UUID (see backend insert contract),
    // so we can fetch /workouts/:plannedId directly (even when /plans/:id/workouts is not available).
    const plannedId = planned?.id ? String(planned.id) : null;
    const executedId = planned?.completed === true && plannedId ? plannedId : null;

    if (!executedId) {
      if (executedPreviewAbortRef.current) executedPreviewAbortRef.current.abort();
      executedPreviewAbortRef.current = null;
      setExecutedPreview({ workoutId: null, debrief: null, loading: false, error: null });
      return;
    }

    if (executedPreview.workoutId && String(executedPreview.workoutId) === String(executedId) && executedPreview.debrief) {
      return;
    }

    if (executedPreviewAbortRef.current) executedPreviewAbortRef.current.abort();
    const controller = new AbortController();
    executedPreviewAbortRef.current = controller;

    setExecutedPreview({ workoutId: executedId, debrief: null, loading: true, error: null });
    fetch(`${API_BASE}/workouts/${executedId}`, { credentials: 'include', signal: controller.signal })
      .then((res) => res.json())
      .then((data) => {
        if (controller.signal.aborted) return;
        setExecutedPreview({
          workoutId: executedId,
          debrief: data?.debrief_data || null,
          loading: false,
          error: null
        });
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setExecutedPreview({ workoutId: executedId, debrief: null, loading: false, error: err?.message || 'Failed to load' });
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkoutForDetails]);

  const handleAnalyzeModification = () => {
      if (!canManagePlan) return;
      if (!modRequest.trim()) return;

      setIsModAnalyzing(true);
      fetch(`${API_BASE}/plans/${planId}/analyze-modification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ request: modRequest, lang: i18n.language })
      })
      .then(res => res.json())
      .then(data => {
          setModAnalysis(data);
          if (Array.isArray(data.suggestions) && data.suggestions.length > 0) {
              setSelectedOption(data.suggestions[0]);
          }
      })
      .catch(err => console.error("Mod analysis failed", err))
      .finally(() => setIsModAnalyzing(false));
  };

  const handleApplyModification = async () => {
      if (!canManagePlan) return;
      if (!selectedOption) return;

      setLastModRequest(modRequest); // Save request text for display
      setIsApplyingMod(true);
      try {
          const isTargeted = selectedOption.kind === 'workout_patches' || modAnalysis?.route === 'targeted';
          const endpoint = isTargeted ? 'apply-workout-patches' : 'apply-modification';
          const res = await fetch(`${API_BASE}/plans/${planId}/${endpoint}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                  modifications: selectedOption.modifications,
                  request_text: modRequest,
                  analysis: modAnalysis
              })
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data?.error || 'Failed to update plan');

          setShowModModal(false);
          setModRequest('');
          setModAnalysis(null);
          setSelectedOption(null);
          if (isTargeted) {
              await fetchPlan();
          } else {
              setPlanData(prev => ({ ...prev, status: 'generating' }));
          }
      } catch (err) {
          console.error('Apply mod failed', err);
          alert(err?.message || 'Failed to update plan');
      } finally {
          setIsApplyingMod(false);
      }
  };

  const saveHrZones = async (zones) => {
    if (!canManagePlan) return;
    setSavingHrZones(true);
    try {
      const res = await fetch(`${API_BASE}/plans/${planId}/hr-zones`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ hr_zones: zones })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to update HR zones');
      setPlanData((prev) => ({ ...prev, hr_zones: data.hr_zones }));
      setShowHrZonesModal(false);
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Failed to update HR zones');
    } finally {
      setSavingHrZones(false);
    }
  };

  const savePowerZones = async (zones) => {
    if (!canManagePlan) return;
    setSavingPowerZones(true);
    try {
      const res = await fetch(`${API_BASE}/plans/${planId}/power-zones`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ power_zones: zones })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to update power zones');
      setPlanData((prev) => ({ ...prev, power_zones: data.power_zones }));
      setShowPowerZonesModal(false);
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Failed to update power zones');
    } finally {
      setSavingPowerZones(false);
    }
  };

  const handleOpenCompletionModal = (workout, weekNumber) => {
      if (!canManagePlan) return;
      // Attach week number to the workout object for context
      setSelectedWorkoutForCompletion({ ...workout, week_number: weekNumber });
      setShowCompletionModal(true);
  };

  const handleOpenWorkoutDetailsModal = (workout, weekNumber) => {
      const next = { ...workout, week_number: weekNumber };
      setSelectedWorkoutForDetails(next);
      const isDesktop = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 768px)').matches;
      if (!isDesktop) setShowWorkoutDetailsModal(true);
  };

  const handleOpenMissedModal = (workout, weekNumber, mode = 'coach') => {
      if (!canManagePlan) return;
      setMissedMode(mode === 'silent' ? 'silent' : 'coach');
      setSelectedWorkoutForMissed({ ...workout, week_number: weekNumber });
      setShowMissedModal(true);
  };

  const handleCompletionSubmit = async (formData) => {
      try {
          const isUnplannedSlot = Boolean(selectedWorkoutForCompletion?.is_unplanned_slot);
          const endpoint = isUnplannedSlot
            ? `${API_BASE}/plans/${planId}/workouts/unplanned-execution`
            : `${API_BASE}/plans/${planId}/workouts/execution`;

          const res = await fetch(endpoint, {
              method: 'POST',
              credentials: 'include',
              body: formData
          });

          if (!res.ok) {
              const errData = await res.json().catch(() => ({}));
              throw new Error(errData?.error || "Execution upload failed");
          }

          const data = await res.json();

          // Redirect to the new workout details page
          navigate(`/workouts/${data.workout_id}`);

      } catch (err) {
          console.error(err);
          alert("Error uploading: " + err.message);
      }
  };

  const handleOpenEditModal = (workout, weekNumber) => {
      if (!canManagePlan) return;
      setSelectedWorkoutForEdit({ ...workout, week_number: weekNumber });
      setShowEditModal(true);
  };

  const handleEditSubmit = async (formData) => {
      try {
          const res = await fetch(`${API_BASE}/plans/${planId}/workouts/update`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                  workout_id: selectedWorkoutForEdit?.id || null,
                  week_number: selectedWorkoutForEdit.week_number,
                  day_name: selectedWorkoutForEdit.day,
                  workout_data: formData
              })
          });

          if (!res.ok) throw new Error("Update failed");

          // Refresh plan data
          const updatedPlan = await fetch(`${API_BASE}/plans/${planId}`, { credentials: 'include' }).then(res => res.json());
          setPlanData(updatedPlan);
          setShowEditModal(false);
          setSelectedWorkoutForEdit(null);

      } catch (err) {
          console.error(err);
          alert("Error updating workout: " + err.message);
      }
  };

  const findPlannedWorkoutById = useCallback((plan, id) => {
    const weeks = plan?.weeks;
    if (!Array.isArray(weeks) || !id) return null;
    for (const week of weeks) {
      const ws = Array.isArray(week?.workouts) ? week.workouts : [];
      for (const w of ws) {
        if (w && String(w.id) === String(id)) return { ...w, week_number: week.week_number };
      }
    }
    return null;
  }, []);

  const handleMoveSubmit = async ({ target_day, target_date, dest_workout_id }) => {
    try {
      const w = selectedWorkoutForDetails;
      if (!w?.id) return;
      if (!target_date) {
        alert('Missing target date. Please ensure the plan has dates for each day of the week.');
        return;
      }

      const res = await fetch(`${API_BASE}/plans/${planId}/workouts/move`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          source_workout_id: w.id,
          target_day,
          target_date,
          dest_workout_id: dest_workout_id || null
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.error || 'Move failed');
      }

      const updatedPlan = await fetch(`${API_BASE}/plans/${planId}`, { credentials: 'include' }).then((r) => r.json());
      setPlanData(updatedPlan);
      setShowMoveModal(false);

      const moved = findPlannedWorkoutById(updatedPlan?.plan, w.id);
      setSelectedWorkoutForDetails(moved);
    } catch (err) {
      console.error(err);
      alert("Error moving workout: " + err.message);
    }
  };

  const findFirstUpcomingPlannedWorkout = useCallback((plan, todayIso) => {
    const weeks = plan?.weeks;
    if (!Array.isArray(weeks)) return null;

    const candidates = [];
    for (const week of weeks) {
      const ws = Array.isArray(week?.workouts) ? week.workouts : [];
      for (const w of ws) {
        if (!w) continue;
        if (w.type === 'rest') continue;
        if (!w.date) continue;
        candidates.push({ ...w, week_number: week.week_number });
      }
    }

    // Prefer upcoming (>= today). If none, fall back to earliest dated workout.
    const upcoming = candidates
      .filter(w => String(w.date) >= String(todayIso))
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));

    if (upcoming.length > 0) return upcoming[0];

    const earliest = candidates
      .slice()
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));

    return earliest[0] || null;
  }, []);

  // Desktop only: if nothing is selected yet, auto-select the first upcoming session.
  useEffect(() => {
    const plan = planData?.plan;
    if (!plan) return;
    if (selectedWorkoutForDetails) return;

    const isDesktop =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(min-width: 768px)').matches;
    if (!isDesktop) return;

    const first = findFirstUpcomingPlannedWorkout(plan, todayDate);
    if (first) setSelectedWorkoutForDetails(first);
  }, [findFirstUpcomingPlannedWorkout, planData?.plan, selectedWorkoutForDetails, todayDate]);

  const toIsoDate = (d) => {
    try {
      return new Date(d).toISOString().split('T')[0];
    } catch (e) {
      return null;
    }
  };

  const handleRevertLastModification = async () => {
    if (reverting) return;
    setReverting(true);
    try {
      const res = await fetch(`${API_BASE}/plans/${planId}/revert-last-modification`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Revert failed');
      setShowHistoryModal(false);
      fetchPlan();
    } catch (e) {
      alert(e?.message || 'Revert failed');
    } finally {
      setReverting(false);
    }
  };

  const handleOpenPlanEndModal = (mode) => {
    if (!canManagePlan) return;
    setPlanEndMode(mode);
    setShowSettingsMenu(false);
    setShowPlanEndModal(true);
  };

  const handleSubmitPlanEnd = async (comment) => {
    if (!canManagePlan || !planEndMode || endingPlan) return;

    setEndingPlan(true);
    try {
      const res = await fetch(`${API_BASE}/plans/${planId}/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          mode: planEndMode,
          comment,
          lang: i18n.language
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to close plan');

      setPlanData((prev) => ({
        ...prev,
        status: data.plan_status,
        ended_at: data.ended_at,
        ended_reason: data.ended_reason,
        ended_comment: data.ended_comment,
        ended_analysis: data.ended_analysis
      }));
      setShowPlanEndModal(false);
      setPlanEndMode(null);
    } catch (e) {
      alert(e?.message || 'Failed to close plan');
    } finally {
      setEndingPlan(false);
    }
  };

  if (loading) return (
      <div className="flex flex-col items-center justify-center min-h-screen text-gray-500">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p>{t('plan.loading')}</p>
      </div>
  );

  if (error) return (
      <div className="flex items-center justify-center min-h-screen">
          <div className="bg-red-50 text-red-800 p-6 rounded-lg max-w-lg text-center">
              <h3 className="font-bold text-lg mb-2">{t('plan.error_loading')}</h3>
              <p>{error}</p>
              <Link to="/" className="mt-4 inline-block text-red-600 hover:underline">{t('plan.return_home')}</Link>
          </div>
      </div>
  );

  const planIsGenerating = planStatus === 'generating' || (planStatus.startsWith('generating_') && planStatus !== 'generating_audit');
  if (planData && planIsGenerating) {
      return <PlanGenerationScreen planData={planData} lastModRequest={lastModRequest || modRequest} />;
  }

  if (!planData || !planData.plan) return <div className="p-10 text-center">No plan data available.</div>;

  const activePlan = planData.plan;
  const { title } = planData;
  const sport = planData.sport || planData.profile?.sport || 'run';
  const isBike = sport === 'bike';
  const hrZones = planData.hr_zones || {};
  const powerZones = planData.power_zones || {};
  const lastPlannedWorkoutDate = getLastPlannedWorkoutDateISO(activePlan);
  const planEvents = Array.isArray(activePlan?.events) ? activePlan.events.filter(Boolean) : [];
  const eventOptionsEvent = eventOptionsId ? planEvents.find((e) => String(e.id) === String(eventOptionsId)) || null : null;
  const eventTitleFor = (w) => {
    const id = w?.event_id || w?.prepared_for_event_id || null;
    if (!id) return null;
    return planEvents.find((e) => String(e.id) === String(id))?.title || null;
  };
  const canMarkPlanCompleted = Boolean(canManagePlan && lastPlannedWorkoutDate && String(todayDate) >= String(lastPlannedWorkoutDate));

  const getWorkoutForDay = (workouts, dayName) => {
      const ws = Array.isArray(workouts) ? workouts : [];
      // Multi workouts per day: return all workouts for this weekday.
      return ws.filter(w => w && w.day === dayName);
  };

  // Executable types: both running and cycling (backward compatible with older bike type enums).
  const runnableTypes = new Set([
    // Run
    'run', 'long_run', 'workout', 'race', 'cross_train', 'cross_train_bike', 'strength',
    // Bike
    'ride_easy', 'long_ride', 'skills', 'strength',
    // Triathlon
    'swim', 'bike', 'brick',
    // Legacy bike enums (older generator versions)
    'sweet_spot', 'threshold', 'intervals'
  ]);

  const completedById = completedWorkouts.reduce((acc, w) => {
    acc[w.id] = w;
    return acc;
  }, {});

  const completedByDate = completedWorkouts.reduce((acc, w) => {
    // Avoid matching unplanned workouts by date to planned sessions.
    if (String(w?.workout_type || '').toLowerCase() === 'unplanned') return acc;
    const key = toIsoDate(w.scheduled_date);
    if (key) acc[key] = w;
    return acc;
  }, {});

  const skippedById = skippedWorkouts.reduce((acc, w) => {
    acc[w.id] = w;
    return acc;
  }, {});

  const skippedByPlannedId = skippedWorkouts.reduce((acc, w) => {
    const plannedId = w?.workout_json?.id ? String(w.workout_json.id) : null;
    if (plannedId) acc[plannedId] = w;
    return acc;
  }, {});

  const skippedByDate = skippedWorkouts.reduce((acc, w) => {
    const key = toIsoDate(w.scheduled_date);
    if (key) acc[key] = w;
    return acc;
  }, {});

  const getActualForPlannedWorkoutHelper = (plannedWorkout) => {
    if (!plannedWorkout) return null;
    if (plannedWorkout.id && completedById[plannedWorkout.id]) {
      return completedById[plannedWorkout.id];
    }
    // Fallback only for legacy planned sessions without ids. With multi workouts/day, date-based matching is ambiguous.
    if (!plannedWorkout.id && plannedWorkout.date && completedByDate[plannedWorkout.date]) {
      return completedByDate[plannedWorkout.date];
    }
    return null;
  };

  const isPlannedWorkoutCompleted = (plannedWorkout) => {
    return plannedWorkout?.completed === true;
  };

  const getSkippedForPlannedWorkoutHelper = (plannedWorkout) => {
    if (!plannedWorkout) return null;
    if (plannedWorkout.skipped_workout_id && skippedById[plannedWorkout.skipped_workout_id]) {
      return skippedById[plannedWorkout.skipped_workout_id];
    }
    if (plannedWorkout.id && skippedByPlannedId[plannedWorkout.id]) {
      return skippedByPlannedId[plannedWorkout.id];
    }
    // Fallback only for legacy planned sessions without ids (date matching is ambiguous with multi workouts/day).
    if (!plannedWorkout.id && plannedWorkout.date && skippedByDate[plannedWorkout.date]) {
      return skippedByDate[plannedWorkout.date];
    }
    // New preferred plan_json flag
    if (plannedWorkout.skipped === true) return { id: 'skipped' };
    return null;
  };

  const getActualStats = (plannedWorkout) => {
    const row = getActualForPlannedWorkoutHelper(plannedWorkout);
    if (!row) return null;
    const summary = row.debrief_data?.gpx_summary;
    return {
      km: summary?.distance_km ?? row.workout_json?.distance_km ?? null,
      min: summary?.duration_seconds ? (summary.duration_seconds / 60) : (row.workout_json?.duration_min ?? null),
      elev: summary?.elevation_gain_m ?? row.workout_json?.altitude_gain_m ?? null
    };
  };

  const calculateWeekPlannedStats = (workouts) => {
    const runnable = workouts.filter(w => runnableTypes.has(w.type));
    const totals = runnable.reduce((acc, w) => ({
      km: acc.km + (w.distance_km || 0),
      elev: acc.elev + (w.altitude_gain_m || 0),
      min: acc.min + (w.duration_min || 0)
    }), { km: 0, elev: 0, min: 0 });
    const count = runnable.length || 0;
    return { ...totals, runs: count, elevAvg: count > 0 ? (totals.elev / count) : 0 };
  };

  const calculateWeekActualStats = (workouts) => {
    const runnable = workouts.filter(w => runnableTypes.has(w.type));
    const completed = runnable
      .map(w => getActualStats(w))
      .filter(Boolean);
    const totals = completed.reduce((acc, s) => ({
      km: acc.km + (s.km || 0),
      elev: acc.elev + (s.elev || 0),
      min: acc.min + (s.min || 0)
    }), { km: 0, elev: 0, min: 0 });
    const count = completed.length || 0;
    return { ...totals, runs: count, elevAvg: count > 0 ? (totals.elev / count) : 0 };
  };

  // Collect all distinct types for dropdown
  const allWorkoutTypes = planData && planData.plan
    ? [...new Set(planData.plan.weeks.flatMap(w => w.workouts.map(wk => wk.type)))]
    : ['run', 'rest', 'workout', 'long_run', 'recovery', 'cross_train'];

  const getDayInitials = () => {
    const lang = (i18n?.language || 'en').toLowerCase();
    if (lang.startsWith('fr')) return ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
    return ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  };


  return (
      <div className={`
        max-w-6xl mx-auto
        ${isMobile ? 'w-full' : 'p-4 md:p-8 bg-white shadow-xl rounded-3xl mt-0 md:mt-10 mb-10 ring-1 ring-gray-900/5'}
        ${isChatTabMobile || isCalendarTabMobile ? 'pb-0' : (isMobile ? 'pb-24' : 'pb-8')}
        relative landscape:max-w-full
      `}>
          {/* Header (hidden on mobile Chat tab to save space) */}
          {!isChatTabMobile ? (
            <div className={`
              z-30 flex flex-row justify-between items-center
              ${isMobile ? 'px-4 pt-2 pb-2 bg-transparent' : 'top-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 pb-6 -mx-4 px-4 -mt-4 pt-4 md:-mx-8 md:px-8 md:-mt-8 md:pt-8 rounded-t-3xl sticky'}
            `}>
                <div className="min-w-0 pr-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <h1 className={`
                        font-black text-gray-900 mb-0 break-words tracking-tight leading-tight
                        ${isMobile ? 'text-2xl' : 'text-3xl md:text-4xl'}
                      `}>{title}</h1>
                      {planIsClosed ? (
                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-wide ${
                          planStatus === 'completed'
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}>
                          {planStatus === 'completed' ? t('plan.end.completed_badge') : t('plan.end.stopped_badge')}
                        </span>
                      ) : null}
                    </div>
                    {planData?.ended_at ? (
                      <div className="mt-2 text-xs font-medium text-gray-500">
                        {t('plan.end.ended_on', { date: new Date(planData.ended_at).toLocaleDateString(i18n.language) })}
                      </div>
                    ) : null}
                </div>
                <div className="flex gap-2 md:gap-3 items-center relative shrink-0">
                    {/* Desktop actions */}
                    {canManagePlan ? (
                      <button
                          onClick={() => setShowModModal(true)}
                          className="hidden md:inline-flex px-5 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 hover:shadow-lg hover:shadow-indigo-500/20 hover:-translate-y-0.5 transition-all font-bold shadow-md items-center gap-2"
                      >
                          <HiPencilSquare className="w-5 h-5" /> {t('plan.adjust_plan')}
                      </button>
                    ) : null}
                    <button
                        onClick={() => setShowHistoryModal(true)}
                        className="hidden md:inline-flex px-4 py-2.5 bg-white border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 hover:text-indigo-600 hover:border-indigo-100 transition-all font-bold shadow-sm items-center gap-2 group"
                    >
                        <span className="text-gray-400 group-hover:text-indigo-500 transition-colors"><HiClock className="w-5 h-5" /></span>
                        {t('plan.history')} <span className="bg-gray-100 text-gray-600 py-0.5 px-2 rounded-md text-xs group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">{planData?.history?.length || 0}</span>
                    </button>
                    {canManagePlan ? (
                      <button
                        onClick={() => setShowCoachChatModal(true)}
                        className="hidden md:inline-flex px-4 py-2.5 bg-white border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 hover:text-indigo-600 hover:border-indigo-100 transition-all font-bold shadow-sm items-center gap-2 group"
                        title={t('chat.open_hint')}
                      >
                        <span className="text-gray-400 group-hover:text-indigo-500 transition-colors"><HiChatBubbleLeftRight className="w-5 h-5" /></span>
                        {t('chat.open')} <span className="bg-gray-100 text-gray-600 py-0.5 px-2 rounded-md text-xs group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">{planData?.chat_message_count || 0}</span>
                      </button>
                    ) : null}

                    {/* Settings menu: owner-only */}
                    {canManagePlan ? (
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowSettingsMenu(v => !v)}
                          className={`
                            p-2.5 rounded-full transition-all shadow-sm
                            ${isMobile ? 'bg-white/80 backdrop-blur-sm border border-gray-200/50 text-gray-600 hover:bg-white' : ''}
                            ${!isMobile && showSettingsMenu ? 'bg-gray-100 border-gray-300 text-gray-900' : ''}
                            ${!isMobile && !showSettingsMenu ? 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700' : ''}
                          `}
                          aria-label={t('plan.settings')}
                          title={t('plan.settings')}
                        >
                          <HiCog6Tooth className="w-5 h-5" />
                        </button>

                        {showSettingsMenu ? (
                          <div className="absolute right-0 mt-3 w-64 bg-white border border-gray-100 rounded-2xl shadow-xl shadow-gray-200/50 overflow-hidden z-50 animate-in fade-in zoom-in-95 origin-top-right">
                            {/* Mobile-only entries */}
                            <button
                              type="button"
                              onClick={() => { setShowSettingsMenu(false); setShowHistoryModal(true); }}
                              className="md:hidden w-full text-left px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                            >
                              <HiClock className="text-gray-400 w-5 h-5" />
                              {t('plan.history')}
                            </button>
                            <button
                              type="button"
                              onClick={() => { setShowSettingsMenu(false); setShowModModal(true); }}
                              className="md:hidden w-full text-left px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                            >
                              <HiPencilSquare className="text-gray-400 w-5 h-5" />
                              {t('plan.adjust_plan')}
                            </button>
                            <button
                              type="button"
                              onClick={() => { setShowSettingsMenu(false); handleMobileTabChange('chat'); }}
                              className="md:hidden w-full text-left px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                            >
                              <HiChatBubbleLeftRight className="text-gray-400 w-5 h-5" />
                              {t('chat.open')}
                            </button>

                            <button
                              type="button"
                              onClick={() => { setShowSettingsMenu(false); setShowAddEventModal(true); }}
                              className="w-full text-left px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                            >
                              <HiFlag className="text-gray-400 w-5 h-5" />
                              {t('events.add_cta')}
                            </button>

                            <div className="md:hidden h-px bg-gray-50 mx-4" />
                            <button
                              type="button"
                              onClick={() => { setShowSettingsMenu(false); setShowVisibilityModal(true); }}
                              className="w-full text-left px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              {t('plan.visibility.title')}
                              <div className="text-xs text-gray-400 font-medium mt-0.5 uppercase tracking-wide">
                                {t(`plan.visibility.${(planData?.visibility || 'open')}.title`)}
                              </div>
                            </button>

                            <div className="h-px bg-gray-50 mx-4" />
                            <button
                              type="button"
                              onClick={() => {
                                setShowSettingsMenu(false);
                                if (isBike) setShowPowerZonesModal(true);
                                else setShowHrZonesModal(true);
                              }}
                              className="w-full text-left px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              {isBike ? t('plan.power_zones') : t('plan.hr_zones')}
                              <div className="text-xs text-gray-400 font-medium mt-0.5 uppercase tracking-wide">
                                {isBike ? t('plan.edit_power_zones') : t('plan.edit_hr_zones')}
                              </div>
                            </button>

                            <div className="h-px bg-gray-50 mx-4" />
                            <button
                              type="button"
                              onClick={() => handleOpenPlanEndModal('stopped')}
                              className="w-full text-left px-4 py-3 text-sm font-bold text-amber-700 hover:bg-amber-50 transition-colors"
                            >
                              {t('plan.end.stop_menu')}
                              <div className="text-xs text-amber-500 font-medium mt-0.5">
                                {t('plan.end.stop_menu_hint')}
                              </div>
                            </button>

                            <div className="h-px bg-gray-50 mx-4" />
                            <button
                              type="button"
                              onClick={() => { setShowSettingsMenu(false); setShowDeletePlanModal(true); }}
                              className="w-full text-left px-4 py-3 text-sm font-bold text-red-600 hover:bg-red-50 transition-colors"
                            >
                              {t('plan.delete.menu')}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                </div>
            </div>
            ) : null}

          {canMarkPlanCompleted ? (
            <div className={`${isMobile ? 'px-4 mb-6' : 'mb-8 mt-6 px-1'}`}>
              <button
                type="button"
                onClick={() => handleOpenPlanEndModal('completed')}
                className="flex w-full items-center justify-center rounded-3xl bg-emerald-600 px-6 py-4 text-center text-base font-black text-white shadow-lg shadow-emerald-500/20 transition hover:-translate-y-0.5 hover:bg-emerald-700 hover:shadow-emerald-500/30 md:text-lg"
              >
                {t('plan.end.complete_cta')}
              </button>
            </div>
          ) : null}

          {planIsClosed ? (
            <div className={`${isMobile ? 'px-4 mb-6' : 'mb-8 mt-6 px-1'}`}>
              <div className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-xs font-black uppercase tracking-widest text-gray-400">
                    {t('plan.end.analysis_title')}
                  </div>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wide ${
                    planStatus === 'completed'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-amber-100 text-amber-800'
                  }`}>
                    {planStatus === 'completed' ? t('plan.end.completed_badge') : t('plan.end.stopped_badge')}
                  </span>
                </div>

                {planData?.ended_comment ? (
                  <div className="mt-4 rounded-2xl border border-gray-100 bg-gray-50 p-4">
                    <div className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-400">
                      {t('plan.end.comment_label')}
                    </div>
                    <div className="whitespace-pre-wrap text-sm text-gray-700">{planData.ended_comment}</div>
                  </div>
                ) : null}

                <div className="mt-4">
                  {planData?.ended_analysis ? (
                    <CoachAnalysisRenderer analysis={planData.ended_analysis} />
                  ) : (
                    <div className="text-sm text-gray-500">{t('plan.end.analysis_empty')}</div>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* HOME TAB (mobile) / always visible (desktop) */}
          {(!isMobile || mobileTab === 'home') ? (
          <>
          {/* Overview Text */}
          <div className={`${isMobile ? 'px-4 mb-6' : 'mb-8 mt-6 md:mb-12 md:mt-8 px-1'}`}>
              <h2 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3 ml-1">{t('plan.coach_strategy')}</h2>
              <div
                className={`
                  relative overflow-hidden cursor-pointer group transition-all duration-300
                  ${isMobile ? 'bg-white rounded-2xl shadow-sm border border-gray-100 p-5' : 'bg-gradient-to-br from-white to-indigo-50/50 p-6 md:p-8 rounded-3xl border border-indigo-100 shadow-sm hover:shadow-md hover:border-indigo-200'}
                `}
                role="button"
                tabIndex={0}
                onClick={() => setIsCoachStrategyCollapsed(v => !v)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setIsCoachStrategyCollapsed(v => !v);
                }}
                aria-label={isCoachStrategyCollapsed ? 'Expand coach strategy' : 'Collapse coach strategy'}
              >
                  {/* Chevron indicator (non-interactive; whole card toggles) */}
                  <div className={`
                    absolute top-4 right-4 p-2 rounded-full transition-colors
                    ${isMobile ? 'bg-gray-50 text-gray-400' : 'bg-white/50 text-indigo-400 group-hover:bg-white group-hover:text-indigo-600 shadow-sm border border-indigo-50'}
                  `}>
                    {isCoachStrategyCollapsed ? (
                      <HiChevronDown className="w-4 h-4" />
                    ) : (
                      <HiChevronUp className="w-4 h-4" />
                    )}
                  </div>

                  <Markdown
                    className={[
                      isCoachStrategyCollapsed ? 'relative line-clamp-2' : '',
                      'text-gray-700 text-sm md:text-lg leading-relaxed font-medium',
                      !isMobile && 'font-serif text-gray-800',
                      '[&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1'
                    ].join(' ')}
                  >
                    {activePlan.plan_overview}
                  </Markdown>

                  {/* Fade to hint there's more when collapsed */}
                  {isCoachStrategyCollapsed && (
                    <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-16 rounded-b-2xl bg-gradient-to-b from-transparent via-white/80 to-white" />
                  )}
              </div>
          </div>

          {/* Mobile: carousel between zones and current week summary */}
          <div className={isMobile ? 'px-4' : ''}>
            <MobilePlanTopCarousel
              isBike={isBike}
              weeks={activePlan.weeks}
              todayDate={todayDate}
              getWeekPlannedStats={calculateWeekPlannedStats}
              getWeekActualStats={calculateWeekActualStats}
              weeklyAnalyses={planData?.weekly_analyses || []}
              planId={planId}
              canEdit={canEdit}
              onWeeklyOptionApplied={fetchPlan}
              plan={activePlan}
            />
          </div>

          {/* Mobile: featured workouts (today + next) under the carousel */}
          {isMobile ? (
            <div className="px-4">
              <FeaturedWorkoutsBlock
                weeks={activePlan.weeks}
                todayDateISO={todayDate}
                workoutColors={WORKOUT_COLORS}
                onWorkoutPress={handleOpenWorkoutDetailsModal}
              />
            </div>
          ) : null}

          {/* Intermediate events (races, big weekends…) */}
          <EventsPanel
            className={isMobile ? 'px-4 mb-6 mt-6' : 'mb-8 mt-2 px-1'}
            planId={planId}
            events={planEvents}
            canEdit={canManagePlan}
            onAdd={() => setShowAddEventModal(true)}
            onOpenOptions={(ev) => ev?.id && setEventOptionsId(ev.id)}
            onChanged={fetchPlan}
          />
          </>
          ) : null}

          {/* CALENDAR TAB (mobile) / always visible (desktop) */}
          {(!isMobile || mobileTab === 'calendar') ? (
          <div className="mb-0 md:mb-8 md:grid md:grid-cols-2 md:gap-6 md:items-stretch">
            <div className={isMobile ? 'bg-white rounded-3xl shadow-sm border border-gray-100 p-4 mx-4' : ''}>
              <CompactTrainingCalendar
                weeks={activePlan.weeks}
                days={DAYS}
                dayInitials={getDayInitials()}
                todayDate={todayDate}
                todayLabel={t('plan.today')}
                workoutColors={WORKOUT_COLORS}
                selectedWorkoutId={selectedWorkoutForDetails?.id || null}
                selectedDateISO={selectedWorkoutForDetails?.is_unplanned_slot ? selectedWorkoutForDetails?.date : null}
                legendItems={
                  isBike
                    ? [
                        { colorClass: WORKOUT_COLORS.ride_easy || 'bg-green-500', label: t('plan.legend.bike.easy') },
                        { colorClass: WORKOUT_COLORS.workout || WORKOUT_COLORS.sweet_spot || 'bg-orange-500', label: t('plan.legend.bike.workout') },
                        { colorClass: WORKOUT_COLORS.long_ride || 'bg-purple-600', label: t('plan.legend.bike.long') },
                        { colorClass: WORKOUT_COLORS.skills || 'bg-blue-400', label: t('plan.legend.bike.skills') },
                        { colorClass: WORKOUT_COLORS.strength || 'bg-amber-500', label: t('plan.legend.bike.strength') }
                      ]
                    : (sport === 'triathlon'
                        ? [
                            { colorClass: WORKOUT_COLORS.swim || 'bg-cyan-500', label: t('plan.legend.triathlon.swim') },
                            { colorClass: WORKOUT_COLORS.ride_easy || 'bg-green-500', label: t('plan.legend.triathlon.bike') },
                            { colorClass: WORKOUT_COLORS.run || 'bg-green-500', label: t('plan.legend.triathlon.run') },
                            { colorClass: WORKOUT_COLORS.brick || 'bg-pink-500', label: t('plan.legend.triathlon.brick') },
                            { colorClass: WORKOUT_COLORS.strength || 'bg-amber-500', label: t('plan.legend.triathlon.strength') }
                          ]
                        : [
                            { colorClass: WORKOUT_COLORS.strength || 'bg-amber-500', label: t('plan.legend.run.strength') },
                            { colorClass: WORKOUT_COLORS.cross_train || 'bg-blue-400', label: t('plan.legend.run.cross_train') },
                            { colorClass: WORKOUT_COLORS.run || 'bg-green-500', label: t('plan.legend.run.easy') },
                            { colorClass: WORKOUT_COLORS.workout || 'bg-orange-500', label: t('plan.legend.run.workout') },
                            { colorClass: WORKOUT_COLORS.long_run || 'bg-purple-600', label: t('plan.legend.run.long') }
                          ]
                      )
                }
                getWorkoutForDay={getWorkoutForDay}
                getActualStats={getActualStats}
                getCompletedId={(w) => getActualForPlannedWorkoutHelper(w)?.id || null}
                getSkippedId={(w) => getSkippedForPlannedWorkoutHelper(w)?.id || null}
                getWeekPlannedStats={calculateWeekPlannedStats}
                events={planEvents}
                onEventPress={(ev) => ev?.id && setEventOptionsId(ev.id)}
                onWorkoutPress={handleOpenWorkoutDetailsModal}
                onMultipleWorkoutsPress={({ weekNumber, dayName, date, workouts }) => {
                  setDayWorkoutsContext({ weekNumber, dayName, date, workouts: Array.isArray(workouts) ? workouts : [] });
                  setShowDayWorkoutsChooser(true);
                }}
                onTodayPress={({ weekNumber, dayName, date }) => {
                  if (!canManagePlan) return;
                  const isDesktop = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 768px)').matches;
                  const slot = {
                    id: `__today__-${date}`,
                    is_unplanned_slot: true,
                    week_number: weekNumber,
                    day: dayName,
                    date,
                    type: 'rest',
                    title: t('plan.selected_workout_panel.unplanned_cta'),
                    description: '',
                    distance_km: 0,
                    duration_min: 0,
                    altitude_gain_m: 0
                  };
                  setSelectedWorkoutForDetails(slot);
                  if (!isDesktop) {
                    // On mobile, open the execution modal directly (same UX as a normal day).
                    setSelectedWorkoutForCompletion(slot);
                    setShowCompletionModal(true);
                  }
                }}
                renderWeekMeta={(week) => (
                  <div className="w-full pr-2">
                    <div className="text-xs font-extrabold text-gray-900">{t('plan.week')} {week.week_number}</div>
                    <div className="text-[10px] font-bold text-indigo-600 uppercase">{week.phase || ''}</div>
                    {week.week_type ? (
                      <div className="text-[10px] text-gray-500 font-bold">{String(week.week_type).replace('_', ' ')}</div>
                    ) : null}
                  </div>
                )}
              />
            </div>

            {/* Desktop: sticky selection panel */}
            <div className="mt-8 md:mt-0 hidden md:block h-full">
              {(() => {
                const hasExecution = Boolean(selectedWorkoutForDetails?.completed === true);

                return (
                  <SelectedWorkoutPanel
                    workout={selectedWorkoutForDetails}
                    // Inject schema version from parent plan for skeleton logic
                    schemaVersion={activePlan?.schema_version || 1}
                    eventTitle={eventTitleFor(selectedWorkoutForDetails)}
                    canEdit={canManagePlan}
                    colorClass={selectedWorkoutForDetails ? (WORKOUT_COLORS[selectedWorkoutForDetails.type] || 'bg-gray-300') : 'bg-gray-300'}
                    isToday={Boolean(selectedWorkoutForDetails?.date && selectedWorkoutForDetails.date === todayDate)}
                    isCompleted={hasExecution}
                    isMissed={Boolean(selectedWorkoutForDetails && getSkippedForPlannedWorkoutHelper(selectedWorkoutForDetails))}
                    isFuture={Boolean(selectedWorkoutForDetails?.date && selectedWorkoutForDetails.date > todayDate)}
                    isPast={Boolean(selectedWorkoutForDetails?.date && selectedWorkoutForDetails.date < todayDate)}
                    actualStats={selectedWorkoutForDetails ? getActualStats(selectedWorkoutForDetails) : null}
                    onClear={() => setSelectedWorkoutForDetails(null)}
                    canViewDetails={hasExecution}
                    executedDebrief={executedPreview?.debrief || null}
                    executedLoading={Boolean(executedPreview?.loading)}
                    executedError={executedPreview?.error || null}
                    onAddAnotherWorkout={
                      canManagePlan && selectedWorkoutForDetails?.date && !selectedWorkoutForDetails?.is_unplanned_slot
                        ? () => {
                            const w = selectedWorkoutForDetails;
                            const slot = {
                              id: `__today__-${w.date}`,
                              is_unplanned_slot: true,
                              week_number: w.week_number,
                              day: w.day,
                              date: w.date,
                              type: 'rest',
                              title: t('plan.selected_workout_panel.unplanned_cta'),
                              description: '',
                              distance_km: 0,
                              duration_min: 0,
                              altitude_gain_m: 0
                            };
                            setSelectedWorkoutForDetails(slot);
                          }
                        : null
                    }
                    onViewDetails={() => {
                      const id = selectedWorkoutForDetails?.id || null;
                      if (id) navigate(`/workouts/${id}`);
                    }}
                    onEdit={() => {
                      if (!selectedWorkoutForDetails) return;
                      handleOpenEditModal(selectedWorkoutForDetails, selectedWorkoutForDetails.week_number);
                    }}
                    onMove={() => {
                      if (!selectedWorkoutForDetails) return;
                      setShowMoveModal(true);
                    }}
                    onComplete={() => {
                      if (!selectedWorkoutForDetails) return;
                      handleOpenCompletionModal(selectedWorkoutForDetails, selectedWorkoutForDetails.week_number);
                    }}
                    onMissed={() => {
                      if (!selectedWorkoutForDetails) return;
                      handleOpenMissedModal(selectedWorkoutForDetails, selectedWorkoutForDetails.week_number, 'silent');
                    }}
                    onAskCoachForMissed={() => {
                      if (!selectedWorkoutForDetails) return;
                      handleOpenMissedModal(selectedWorkoutForDetails, selectedWorkoutForDetails.week_number, 'coach');
                    }}
                    onCreateUnplanned={() => {
                      if (!selectedWorkoutForDetails) return;
                      // Open the same execution modal as a planned workout.
                      setSelectedWorkoutForCompletion(selectedWorkoutForDetails);
                      setShowCompletionModal(true);
                    }}
                  />
                );
              })()}
            </div>
          </div>
          ) : null}

          {/* CHAT TAB (mobile): fixed full-height panel, only message list scrolls */}
          {isChatTabMobile ? (
            <div
              className={[
                'fixed left-0 right-0 z-30 bg-white',
                'top-[calc(72px+env(safe-area-inset-top))]',
                'bottom-[calc(56px+env(safe-area-inset-bottom))]',
                'md:hidden'
              ].join(' ')}
            >
              <div className="h-full min-h-0 flex flex-col">
                <CoachChatPanel
                  isActive
                  planId={planId}
                  canEdit={canManagePlan}
                  lang={i18n.language}
                  onPlanChanged={fetchPlan}
                  plan={activePlan}
                />
              </div>
            </div>
          ) : null}

          {/* HISTORY MODAL */}
          <PlanHistoryModal
            isOpen={showHistoryModal}
            onClose={() => setShowHistoryModal(false)}
            history={planData?.history || []}
            onRevert={handleRevertLastModification}
            canEdit={canManagePlan}
          />

          {/* MODIFICATION MODAL */}
          {showModModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                  <div className="bg-white rounded-xl shadow-2xl w-full max-w-full max-h-[90vh] overflow-y-auto md:max-w-lg">
                      <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                          <h3 className="text-xl font-bold text-gray-800">{t('modals.adjust_title')}</h3>
                          <button onClick={() => setShowModModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                      </div>

                      <div className="p-6">
                          {!modAnalysis ? (
                              <>
                                  <label className="block text-sm font-bold text-gray-700 mb-2">
                                      {t('modals.what_change')}
                                  </label>
                                  <textarea
                                      className="w-full border border-gray-300 rounded-lg p-3 h-32 mb-4 focus:ring-2 focus:ring-indigo-500 outline-none"
                                      placeholder={t('modals.placeholder_change')}
                                      value={modRequest}
                                      onChange={(e) => setModRequest(e.target.value)}
                                  ></textarea>
                                  <div className="flex justify-end">
                                      <button
                                          onClick={handleAnalyzeModification}
                                          disabled={isModAnalyzing || !modRequest.trim()}
                                          className={`px-6 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition ${isModAnalyzing ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      >
                                          {isModAnalyzing ? t('workout.analyzing') : t('modals.analyze_request')}
                                      </button>
                                  </div>
                              </>
                          ) : (
                              <div className="animate-fade-in">
                                  <div className={`p-4 rounded-lg mb-4 border-l-4 ${
                                      modAnalysis.status === 'feasible' ? 'bg-green-50 border-green-500 text-green-800' :
                                      modAnalysis.status === 'risky' ? 'bg-yellow-50 border-yellow-500 text-yellow-800' :
                                      'bg-red-50 border-red-500 text-red-800'
                                  }`}>
                                      <div className="font-bold capitalize mb-1">{modAnalysis.status} Request</div>
                                      <div className="text-sm">{modAnalysis.analysis}</div>
                                  </div>

                                  <h4 className="font-bold text-sm text-gray-600 mb-2">{t('modals.coach_suggestions')}</h4>
                                  <div className="space-y-3 mb-6">
                                      {modAnalysis.suggestions.map(opt => (
                                          <button
                                              key={opt.id}
                                              onClick={() => setSelectedOption(opt)}
                                              className={`w-full text-left p-3 rounded-lg border-2 transition ${
                                                  selectedOption?.id === opt.id
                                                  ? 'border-indigo-600 bg-indigo-50'
                                                  : 'border-gray-200 hover:border-indigo-300'
                                              }`}
                                          >
                                              <div className="font-bold text-indigo-900 text-sm">{opt.label}</div>
                                              <div className="text-xs text-gray-600">{opt.description}</div>
                                          </button>
                                      ))}
                                  </div>

                                  <div className="flex justify-between items-center border-t border-gray-100 pt-4">
                                      <button
                                          onClick={() => setModAnalysis(null)}
                                          className="text-gray-500 hover:text-gray-700 text-sm underline"
                                      >
                                          Back
                                      </button>
                                      <button
                                          onClick={handleApplyModification}
                                          disabled={!selectedOption || isApplyingMod}
                                          className={`px-6 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition ${(!selectedOption || isApplyingMod) ? 'opacity-50' : ''}`}
                                      >
                                          {isApplyingMod ? 'Updating Plan...' : 'Apply Changes'}
                                      </button>
                                  </div>
                              </div>
                          )}
                      </div>
                  </div>
              </div>
          )}

          {/* COMPLETION MODAL */}
          {selectedWorkoutForCompletion && (
              <ExecutionModal
                  isOpen={showCompletionModal}
                  onClose={() => setShowCompletionModal(false)}
                  workout={selectedWorkoutForCompletion}
                  onConfirm={handleCompletionSubmit}
                  planSport={sport}
              />
          )}

          {/* WORKOUT DETAILS MODAL */}
          {selectedWorkoutForDetails && (
            <WorkoutDetailModal
              isOpen={showWorkoutDetailsModal}
              onClose={() => { setShowWorkoutDetailsModal(false); setSelectedWorkoutForDetails(null); }}
              workout={selectedWorkoutForDetails}
              schemaVersion={activePlan?.schema_version || 1}
              eventTitle={eventTitleFor(selectedWorkoutForDetails)}
              colorClass={WORKOUT_COLORS[selectedWorkoutForDetails.type] || 'bg-gray-300'}
              isToday={selectedWorkoutForDetails.date === todayDate}
              isCompleted={isPlannedWorkoutCompleted(selectedWorkoutForDetails)}
              isMissed={!!getSkippedForPlannedWorkoutHelper(selectedWorkoutForDetails)}
              completedId={getActualForPlannedWorkoutHelper(selectedWorkoutForDetails)?.id || selectedWorkoutForDetails.id || null}
              actualStats={getActualStats(selectedWorkoutForDetails)}
              onAddAnotherWorkout={
                canManagePlan && selectedWorkoutForDetails?.date && !selectedWorkoutForDetails?.is_unplanned_slot
                  ? () => {
                      const w = selectedWorkoutForDetails;
                      setShowWorkoutDetailsModal(false);
                      const slot = {
                        id: `__today__-${w.date}`,
                        is_unplanned_slot: true,
                        week_number: w.week_number,
                        day: w.day,
                        date: w.date,
                        type: 'rest',
                        title: t('plan.selected_workout_panel.unplanned_cta'),
                        description: '',
                        distance_km: 0,
                        duration_min: 0,
                        altitude_gain_m: 0
                      };
                      setSelectedWorkoutForDetails(slot);
                      // On mobile, open the execution modal directly.
                      setSelectedWorkoutForCompletion(slot);
                      setShowCompletionModal(true);
                    }
                  : null
              }
              onEdit={
                !canManagePlan || (selectedWorkoutForDetails.date && selectedWorkoutForDetails.date < todayDate) || !!getSkippedForPlannedWorkoutHelper(selectedWorkoutForDetails)
                  ? null
                  : () => {
                      setShowWorkoutDetailsModal(false);
                      handleOpenEditModal(selectedWorkoutForDetails, selectedWorkoutForDetails.week_number);
                    }
              }
              onMove={
                !canManagePlan ||
                isPlannedWorkoutCompleted(selectedWorkoutForDetails) ||
                !!getSkippedForPlannedWorkoutHelper(selectedWorkoutForDetails)
                  ? null
                  : () => {
                      setShowWorkoutDetailsModal(false);
                      setShowMoveModal(true);
                    }
              }
              onMarkComplete={
                canManagePlan &&
                runnableTypes.has(selectedWorkoutForDetails.type) &&
                (!selectedWorkoutForDetails?.date || selectedWorkoutForDetails.date <= todayDate)
                  ? (getSkippedForPlannedWorkoutHelper(selectedWorkoutForDetails)
                      ? null
                      : () => {
                          setShowWorkoutDetailsModal(false);
                          handleOpenCompletionModal(selectedWorkoutForDetails, selectedWorkoutForDetails.week_number);
                        })
                  : null
              }
              onMarkMissed={
                canManagePlan &&
                runnableTypes.has(selectedWorkoutForDetails.type) &&
                (!selectedWorkoutForDetails?.date || selectedWorkoutForDetails.date <= todayDate)
                  ? () => {
                      setShowWorkoutDetailsModal(false);
                      handleOpenMissedModal(selectedWorkoutForDetails, selectedWorkoutForDetails.week_number, 'silent');
                    }
                  : null
              }
              onAskCoachForMissed={
                canManagePlan &&
                runnableTypes.has(selectedWorkoutForDetails.type) &&
                (!selectedWorkoutForDetails?.date || selectedWorkoutForDetails.date <= todayDate)
                  ? () => {
                      setShowWorkoutDetailsModal(false);
                      handleOpenMissedModal(selectedWorkoutForDetails, selectedWorkoutForDetails.week_number, 'coach');
                    }
                  : null
              }
              onViewDetails={() => {
                const id = getActualForPlannedWorkoutHelper(selectedWorkoutForDetails)?.id || selectedWorkoutForDetails.id;
                if (id) navigate(`/workouts/${id}`);
              }}
            />
          )}

          {/* DAY WORKOUTS CHOOSER MODAL */}
          {dayWorkoutsContext ? (
            <DayWorkoutsChooserModal
              isOpen={showDayWorkoutsChooser}
              onClose={() => { setShowDayWorkoutsChooser(false); setDayWorkoutsContext(null); }}
              dayName={dayWorkoutsContext.dayName}
              dateISO={dayWorkoutsContext.date}
              workouts={dayWorkoutsContext.workouts}
              workoutColors={WORKOUT_COLORS}
              canEdit={canManagePlan}
              onSelectWorkout={(w) => {
                const ctx = dayWorkoutsContext;
                setShowDayWorkoutsChooser(false);
                setDayWorkoutsContext(null);
                if (!ctx) return;
                handleOpenWorkoutDetailsModal(w, ctx.weekNumber);
              }}
              onAddWorkout={() => {
                if (!canManagePlan) return;
                const ctx = dayWorkoutsContext;
                setShowDayWorkoutsChooser(false);
                setDayWorkoutsContext(null);
                if (!ctx) return;
                const slot = {
                  id: `__today__-${ctx.date}`,
                  is_unplanned_slot: true,
                  week_number: ctx.weekNumber,
                  day: ctx.dayName,
                  date: ctx.date,
                  type: 'rest',
                  title: t('plan.selected_workout_panel.unplanned_cta'),
                  description: '',
                  distance_km: 0,
                  duration_min: 0,
                  altitude_gain_m: 0
                };
                setSelectedWorkoutForDetails(slot);
                if (!isDesktop) {
                  setSelectedWorkoutForCompletion(slot);
                  setShowCompletionModal(true);
                }
              }}
            />
          ) : null}

          {/* MOVE WORKOUT MODAL */}
          {selectedWorkoutForDetails && (
            <MoveWorkoutModal
              isOpen={showMoveModal}
              onClose={() => setShowMoveModal(false)}
              workout={selectedWorkoutForDetails}
              week={activePlan.weeks.find((w) => w.week_number === selectedWorkoutForDetails.week_number) || null}
              days={DAYS}
              todayDateISO={todayDate}
              onConfirm={handleMoveSubmit}
            />
          )}

          {/* MISSED WORKOUT MODAL */}
          {selectedWorkoutForMissed && (
            <MissedWorkoutModal
              isOpen={showMissedModal}
              onClose={() => { setShowMissedModal(false); setSelectedWorkoutForMissed(null); setMissedMode('coach'); }}
              workout={selectedWorkoutForMissed}
              askCoach={missedMode === 'coach'}
              onConfirm={async (reason) => {
                const res = await fetch(`${API_BASE}/plans/${planId}/workouts/missed`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({
                    workout_id: selectedWorkoutForMissed?.id || null,
                    week_number: selectedWorkoutForMissed.week_number,
                    day_name: selectedWorkoutForMissed.day,
                    reason,
                    ask_coach: missedMode === 'coach',
                    lang: i18n.language
                  })
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data?.error || 'Failed to mark missed');

                // Refresh plan + event lists so UI shows the missed marker
                fetchPlan();

                if (data?.coach?.version === 2) {
                  // Coach v2: 0-2 concrete options (move / merge / let it go).
                  setMissedCoach({
                    analysis: data.coach,
                    sourceId: data.skipped_workout_id,
                    title: selectedWorkoutForMissed?.title || null,
                    date: selectedWorkoutForMissed?.date || null
                  });
                } else if (data?.coach) {
                  // Legacy shape: open the existing coach suggestion flow (same UI as manual plan modification)
                  setModRequest(data.request_text || '');
                  setModAnalysis(data.coach);
                  if (data.coach?.suggestions?.length > 0) setSelectedOption(data.coach.suggestions[0]);
                  if (canManagePlan) setShowModModal(true);
                }
              }}
            />
          )}

          {/* EDIT WORKOUT MODAL */}
          <EditWorkoutModal
              isOpen={showEditModal}
              onClose={() => setShowEditModal(false)}
              workout={selectedWorkoutForEdit}
              existingTypes={allWorkoutTypes}
              onConfirm={handleEditSubmit}
          />

          <HrZonesModal
            isOpen={showHrZonesModal}
            onClose={() => setShowHrZonesModal(false)}
            zones={hrZones}
            onSave={saveHrZones}
            saving={savingHrZones}
          />

          <PowerZonesModal
            isOpen={showPowerZonesModal}
            onClose={() => setShowPowerZonesModal(false)}
            zones={powerZones}
            onSave={savePowerZones}
            saving={savingPowerZones}
          />

          <PlanVisibilityModal
            isOpen={showVisibilityModal}
            onClose={() => setShowVisibilityModal(false)}
            value={planData?.visibility || 'open'}
            canEdit={canManagePlan}
            saving={savingVisibility}
            onSave={saveVisibility}
          />

          <PlanEndModal
            isOpen={showPlanEndModal}
            mode={planEndMode}
            saving={endingPlan}
            onClose={() => {
              if (endingPlan) return;
              setShowPlanEndModal(false);
              setPlanEndMode(null);
            }}
            onConfirm={handleSubmitPlanEnd}
          />

          <DeletePlanModal
            isOpen={showDeletePlanModal}
            saving={deletingPlan}
            onClose={() => {
              if (deletingPlan) return;
              setShowDeletePlanModal(false);
            }}
            onConfirm={async () => {
              if (deletingPlan) return;
              setDeletingPlan(true);
              try {
                const res = await fetch(`${API_BASE}/plans/${planId}`, {
                  method: 'DELETE',
                  credentials: 'include'
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data?.error || 'Failed');
                setShowDeletePlanModal(false);
                navigate('/');
              } catch (e) {
                alert(e?.message || 'Failed to delete plan');
              } finally {
                setDeletingPlan(false);
              }
            }}
          />

          <CoachChatDrawer
            isOpen={showCoachChatModal && isDesktop}
            onClose={() => setShowCoachChatModal(false)}
            planId={planId}
            canEdit={canManagePlan}
            lang={i18n.language}
            onPlanChanged={fetchPlan}
            plan={activePlan}
          />

          <AddEventModal
            isOpen={showAddEventModal}
            onClose={() => setShowAddEventModal(false)}
            planId={planId}
            defaultSport={sport}
            canEdit={canManagePlan}
            plan={activePlan}
            onCreated={() => fetchPlan()}
            onApplied={() => fetchPlan()}
          />

          <CoachOptionsModal
            isOpen={Boolean(eventOptionsEvent)}
            onClose={() => setEventOptionsId(null)}
            title={eventOptionsEvent?.title || ''}
            subtitle={eventOptionsEvent
              ? `${formatShortDate(eventOptionsEvent.start_date, i18n.language)}${eventOptionsEvent.end_date && eventOptionsEvent.end_date !== eventOptionsEvent.start_date ? ` → ${formatShortDate(eventOptionsEvent.end_date, i18n.language)}` : ''} · ${t(`events.priority.${eventOptionsEvent.priority || 'B'}.short`)}`
              : null}
            analysis={eventOptionsEvent?.replan || null}
            decision={eventOptionsEvent?.replan?.decision || null}
            planId={planId}
            source="event"
            sourceId={eventOptionsEvent?.id || null}
            canEdit={canManagePlan}
            plan={activePlan}
            onApplied={() => fetchPlan()}
          >
            {eventOptionsEvent?.notes ? (
              <div className="mb-5 rounded-2xl border border-gray-100 bg-white p-4 text-sm text-gray-600 whitespace-pre-line">
                {eventOptionsEvent.notes}
              </div>
            ) : null}
          </CoachOptionsModal>

          <CoachOptionsModal
            isOpen={Boolean(missedCoach)}
            onClose={() => setMissedCoach(null)}
            title={t('coach_options.missed_title')}
            subtitle={missedCoach ? [missedCoach.title, missedCoach.date ? formatShortDate(missedCoach.date, i18n.language) : null].filter(Boolean).join(' · ') : null}
            analysis={missedCoach?.analysis || null}
            planId={planId}
            source="workout"
            sourceId={missedCoach?.sourceId || null}
            canEdit={canManagePlan}
            plan={activePlan}
            onApplied={() => fetchPlan()}
          />

          {isMobile ? (
            <MobileBottomTabs value={mobileTab} onChange={handleMobileTabChange} />
          ) : null}
      </div>
  );
};

export default PlanView;
